<template>
  <div class="admin-dashboard">
    <header class="dash-header">
      <h2>Dashboard</h2>
      <div class="dash-right">
        <div class="dash-welcome">
          Welcome, <span class="username">{{ currentUsername }}</span>
        </div>
        <div class="dash-meta">
          <button class="btn-ghost" @click="refreshAll" :disabled="loading">
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
          <el-dropdown @command="handleUserMenuCommand">
            <button class="btn-ghost">
              <div style="display: flex; align-items: center; gap: 4px;">
                <div v-if="currentUserIsAdmin"><el-icon ><UserFilled /></el-icon></div>
                <div v-else><el-icon ><User /></el-icon></div>
                <div style="margin-top: -3px;">Account</div>
                <div><el-icon><ArrowDown /></el-icon></div>
              </div>
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="changePassword">
                  <el-icon><Lock /></el-icon>&nbsp;Change Password
                </el-dropdown-item>
                <el-dropdown-item command="signOut" divided>
                  <el-icon><SwitchButton /></el-icon>&nbsp;Sign Out
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </header>

    <!-- Loading state -->
    <div v-if="loading && stats.totalViews === 0" class="loading-state">
      <div class="spinner"></div>
      <p>Loading dashboard...</p>
    </div>

    <!-- Error alert -->
    <div v-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <!-- Statistics / default view -->
    <section class="stats-grid" v-if="!error">
      <div class="stat-card clickable" @click="goToDetails('pageviews')">
        <div class="stat-header">
          <div class="stat-title"><el-icon><Histogram /></el-icon>&nbsp;Page Views (7d)</div>          
        </div>
        <div class="stat-value">{{ stats.totalViews }}</div>
        <div class="sparkline">
          <div v-for="(v,i) in stats.series" :key="i" class="bar" :style="{ height: v + '%' }"></div>
        </div>
        <div class="stat-foot">Unique visitors: {{ stats.unique }}</div>
      </div>

      <div class="stat-card clickable" @click="goToDetails('users')" v-if="currentUserIsAdmin">
        <div class="stat-header">
          <div class="stat-title"><el-icon><User /></el-icon>&nbsp;Users</div>          
        </div>
        <div class="stat-value">{{ users.totalCount }}</div>
        <div class="stat-foot">Deactivated: {{ users.disabledCount }}</div>
        <div class="stat-foot">
          <a href="#" @click.prevent.stop="toggleActiveUsers" class="active-users-link">
            Active: {{ users.activeUsersList.length }}
            <el-icon class="toggle-icon" :class="{ rotated: showActiveUsers }">
              <ArrowDown />
            </el-icon>
          </a>
        </div>

        <!-- Active users dropdown -->
        <transition name="slide-fade">
          <div v-if="showActiveUsers" class="active-users-dropdown">
            <div v-if="users.activeUsersList && users.activeUsersList.length > 0" class="users-list">
              <div v-for="activeUser in users.activeUsersList" :key="activeUser.userId" class="user-item">
                <div class="user-item-name">{{ activeUser.userName }}</div>
                <div class="user-item-time">{{ formatLastLogin(activeUser.lastLoginTime) }}</div>
              </div>
            </div>
            <div v-else class="empty-list">No active users</div>
          </div>
        </transition>
      </div>

      <div class="stat-card clickable" @click="goToDetails('news')">
        <div class="stat-header">
          <div class="stat-title"><el-icon><Document /></el-icon>&nbsp;News</div>          
        </div>
        <div class="stat-value">{{ news.totalCount }}</div>
        <div class="stat-foot">Disabled: {{ news.disabledCount }}</div>
      </div>

      <div class="stat-card clickable" @click="goToDetails('projects')">
        <div class="stat-header">
          <div class="stat-title"><el-icon><OfficeBuilding /></el-icon>&nbsp;Projects</div>          
        </div>
        <div class="stat-value">{{ projects.totalCount }}</div>
        <div class="stat-foot">Disabled: {{ projects.disabledCount }}</div>
        <div class="stat-foot">Video Projects: {{ projects.videoCount }}</div>
      </div>
    </section>    

    <!-- Change Password Dialog -->
    <el-dialog
      v-model="showPasswordDialog"
      title="Change Password"
      width="450px"
      :close-on-click-modal="false"      
    >
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef" label-width="140px">
        <el-form-item label="Current Password" prop="currentPassword">
          <el-input
            v-model="passwordForm.currentPassword"
            type="password"
            placeholder="Enter current password"
            show-password
          />
        </el-form-item>
        <el-form-item label="New Password" prop="newPassword">
          <el-input
            v-model="passwordForm.newPassword"
            type="password"
            placeholder="Enter new password"
            show-password
          />
        </el-form-item>
        <el-form-item label="Confirm" prop="confirmPassword">
          <el-input
            v-model="passwordForm.confirmPassword"
            type="password"
            placeholder="Re-enter new password"
            show-password
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showPasswordDialog = false">Cancel</el-button>
          <el-button type="primary" @click="handleChangePassword" :loading="passwordChanging">
            Confirm
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import store from '@/store';
import { ref, onMounted, computed, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import apiClient from '@/apiClient';

const router = useRouter();

// state
const loading = ref(true);
const error = ref('');
const showActiveUsers = ref(false);
const showPasswordDialog = ref(false);
const passwordChanging = ref(false);
const passwordFormRef = ref(null);

const { proxy } = getCurrentInstance();


// Password form
const passwordForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

// Password validation rules
const validateConfirmPassword = (rule, value, callback) => {
  if (value !== passwordForm.value.newPassword) {
    callback(new Error('Passwords do not match'));
  } else {
    callback();
  }
};

const passwordRules = {
  currentPassword: [
    { required: true, message: 'Please enter current password', trigger: 'blur' }
  ],
  newPassword: [
    { required: true, message: 'Please enter new password', trigger: 'blur' },
    { min: 6, message: 'Password must be at least 6 characters', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: 'Please confirm new password', trigger: 'blur' },
    { validator: validateConfirmPassword, trigger: 'blur' }
  ]
};

// Get current username from store
const currentUsername = computed(() => {
  return store.state.user?.userName || 'Guest';
});

const currentUserIsAdmin = computed(() => {
  return store.state.user?.isAdmin || false;
});

// mock placeholders; replace with real data from API
const stats = ref({
  totalViews: 0,
  unique: 0,
  series: [0, 0, 0, 0, 0, 0, 0],
});
const users = ref({
  totalCount: 0,
  disabledCount: 0,
  activeUsersList: []
});
const news = ref({
  totalCount: 0,
  disabledCount: 0
});
const projects = ref({
  totalCount: 0,
  disabledCount: 0,
  videoCount: 0
});

// Toggle active users dropdown
function toggleActiveUsers() {
  showActiveUsers.value = !showActiveUsers.value;
}

// Format last login time
function formatLastLogin(dateStr) {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Load dashboard summary data from backend
async function loadDashboardInfo() {
  loading.value = true;
  error.value = '';
  try {
    const response = await apiClient.post('/Admin/getDashboardInfo', {
      accountId: store.state.user?.userId || 0
    });
    if (response && response.data) {
      const data = response.data;
      
      // Update stats
      if (data.stats) {
        stats.value = {
          totalViews: data.stats.totalViews || 0,
          unique: data.stats.unique || 0,
          series: data.stats.series || [0, 0, 0, 0, 0, 0, 0],
        };
      }
      
      // Update users summary
      if (data.users) {
        users.value = {
          totalCount: data.users.totalCount || 0,
          disabledCount: data.users.disabledCount || 0,
          activeUsersList: data.users.activeUsers || []
        };
      }
      
      // Update news summary
      if (data.news) {
        news.value = {
          totalCount: data.news.totalCount || 0,
          disabledCount: data.news.disabledCount || 0
        }
      }
      
      // Update projects summary
      if (data.projects) {
        projects.value = {
          totalCount: data.projects.totalCount || 0,
          disabledCount: data.projects.disabledCount || 0,
          videoCount: data.projects.videoCount || 0
        }
      }
    }
  } catch (e) {
    error.value = (e?.response?.data?.msg) || 'Failed to load dashboard data';    
       
  } finally {
    loading.value = false;
  }
}

// basic actions (connect to apiClient as needed)
function refreshAll() {
  loadDashboardInfo();
}

// Handle user menu commands
function handleUserMenuCommand(command) {
  if (command === 'signOut') {
    signOut();
  } else if (command === 'changePassword') {
    openChangePasswordDialog();
  }
}

// Open change password dialog
function openChangePasswordDialog() {
  showPasswordDialog.value = true;
  // Reset form
  passwordForm.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };
  if (passwordFormRef.value) {
    passwordFormRef.value.clearValidate();
  }
}

// Handle change password
async function handleChangePassword() {
  if (!passwordFormRef.value) return;
  
  await passwordFormRef.value.validate(async (valid) => {
    if (!valid) return;
    
    passwordChanging.value = true;
    try {
       const response = await apiClient.post('/Admin/changeAccountPassword', {        
        accountId: 0,
        oldPassword: passwordForm.value.currentPassword,
        newPassword: passwordForm.value.newPassword
      });
      
      if (response && response.data) {              
        proxy.$handleTips(response.data.tag, response.data.msg, error);
        showPasswordDialog.value = false;
        
        // Reset form
        passwordForm.value = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        };
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.msg || 'Failed to change password';      
      proxy.$handleTips(e.response.data.tag, errorMsg, e);
    } finally {
      passwordChanging.value = false;
    }
  });
}

// sign out: clear token and go to login
function signOut() {
  try { 
    store.dispatch('logout');
  } catch (e) { console.error(e); }
  router.replace({ name: 'AdminLogin' }).catch(() => {
    router.replace({ name: 'Home' }).catch(()=>{});
  });
}

// navigate to detailed stats page
async function goToDetails(type) {
  if(!type) return; 
  if(type !== 'pageviews' && type !== 'users' && type !== 'news' && type !== 'projects') return;
  if(type === 'users') {
    try {
      await apiClient.post('/Admin/checkAdminAccount',);
    } catch (e) {
      error.value = (e?.response?.data?.msg) || 'Permission denied, admin only.';
      return;
    }

    router.push({ name: 'AdminUserList' } ).catch(() => {});
  }
  else if(type === 'news') {
    router.push({ name: 'AdminNewsList' }).catch(() => {});
  } 
  if(type === 'projects') {
     router.push({ name: 'AdminExpList' }).catch(() => {}); 
  }
}

onMounted(() => {
  // initial load from backend
  loadDashboardInfo();
});
</script>

<style>
.admin-dashboard {
  padding: 24px;
  color: #e6f0f2;
  min-height: calc(80vh - 84px);
  background: linear-gradient(180deg,#071018 0%, rgba(7,16,24,0.6) 100%);
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
}

/* header */
.dash-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  gap: 16px;
  flex-wrap: wrap;
}
.dash-header h2 { 
  color: #13c2c2; 
  margin: 0; 
  font-weight: 700;
  font-size: 1.5rem;
}

/* Right section with welcome and actions */
.dash-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

/* Welcome message */
.dash-welcome {
  color: #fff;
  font-size: 0.95rem;
  font-weight: 500;
  white-space: nowrap;
  padding-right: 18px;
}
.dash-welcome .username {
  color: #fff;
  font-weight: 700;
}

.dash-meta {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* stats grid */
.stats-grid {
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap:16px;
  margin-bottom:20px;
}

/* base stat card style */
.stat-card {
  border: 1px solid rgba(255,255,255,0.03);
  padding:14px;
  border-radius:8px;
  box-shadow: 0 10px 30px rgba(2,8,12,0.45);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

/* clickable card hover effect */
.stat-card.clickable {
  cursor: pointer;
}
.stat-card.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 40px rgba(2,8,12,0.6);
}

/* stat card header with two-end alignment */
.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.stat-title {
  display: flex;
  align-items: center;
  color: rgba(255,255,255,0.7);
  font-size: 0.85rem;
}

/* Details button */
.btn-details {
  background: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.8);
  border: 1px solid rgba(255,255,255,0.08);
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
}
.btn-details:hover {
  background: rgba(255,255,255,0.1);
  color: #fff;
  border-color: rgba(255,255,255,0.15);
}

.stat-value {
  font-size: 1.6rem;
  color: #fff;
  font-weight: 700;
  margin-bottom: 8px;
}

.stat-foot {
  color: rgba(255,255,255,0.55);
  font-size: 0.85rem;
  margin-top: 8px;
}

/* distinct and more visible dark backgrounds for each card */
.stat-card:nth-child(1) {
  background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01));
  border-color: rgba(255,255,255,0.04);
}
.stat-card:nth-child(2) {
  background: linear-gradient(180deg, rgba(19,194,194,0.15), rgba(5,166,166,0.08));
  border-color: rgba(19,194,194,0.25);
}
.stat-card:nth-child(3) {
  background: linear-gradient(180deg, rgba(33,150,243,0.15), rgba(21,101,192,0.08));
  border-color: rgba(33,150,243,0.25);
}
.stat-card:nth-child(4) {
  background: linear-gradient(180deg, rgba(156,39,176,0.15), rgba(123,31,162,0.08));
  border-color: rgba(156,39,176,0.25);
}

/* sparkline bars */
.sparkline { display:flex; gap:4px; align-items:end; height:48px; }
.sparkline .bar {
  width:10px;
  background: linear-gradient(180deg,#13c2c2,#05a6a6);
  border-radius:3px 3px 0 0;
  opacity:0.95;
}

/* panels */
.panel-row { display:grid; grid-template-columns: repeat(3,1fr); gap:16px; }
.panel {
  background: linear-gradient(180deg, rgba(255,255,255,0.01), rgba(255,255,255,0.005));
  border: 1px solid rgba(255,255,255,0.03);
  border-radius:8px;
  overflow:hidden;
}
.panel-head {
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:12px 14px;
  border-bottom:1px solid rgba(255,255,255,0.02);
}
.panel-head h3 { margin:0; color:#bfeff0; font-size:1rem; }
.panel-actions { display:flex; gap:8px; align-items:center; }
.input-sm { padding:6px 8px; border-radius:6px; border:1px solid rgba(255,255,255,0.04); background: rgba(0,0,0,0.25); color:#fff; }

/* table */
.table { width:100%; border-collapse:collapse; font-size:0.92rem; color:#e6f0f2; }
.table thead th { text-align:left; padding:10px 14px; font-size:0.82rem; color:rgba(255,255,255,0.7); border-bottom:1px solid rgba(255,255,255,0.02); }
.table tbody td { padding:10px 14px; border-bottom:1px dashed rgba(255,255,255,0.02); }
.actions { display:flex; gap:8px; justify-content:flex-end; }

/* buttons */
.btn { background: rgba(255,255,255,0.04); color:#bfeff0; padding:6px 10px; border-radius:6px; border:none; cursor:pointer; }
.btn-ghost { background:transparent; color:#9fdfe0; border:1px solid rgba(255,255,255,0.02); padding:6px 8px; border-radius:6px; cursor:pointer; }
.btn-ghost.danger { color:#ff8b8b; border-color: rgba(255,139,139,0.07); }

/* badges */
.badge { padding:4px 8px; border-radius:999px; font-size:0.78rem; }
.badge-green { background: rgba(19,194,194,0.12); color:#13c2c2; }
.badge-muted { background: rgba(255,255,255,0.02); color: rgba(255,255,255,0.6); }

/* footer */
.dash-footer { margin-top:18px; color:rgba(255,255,255,0.35); font-size:0.85rem; }

/* responsive */
@media (max-width: 1100px) {
  .panel-row { grid-template-columns: repeat(1,1fr); }
  .stats-grid { grid-template-columns: repeat(2,1fr); }
}
@media (max-width: 640px) {
  .stats-grid { grid-template-columns: 1fr; }
}

/* Responsive: stack on small screens */
@media (max-width: 768px) {
  .dash-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .dash-header h2 {
    font-size: 1.25rem;
  }
  .dash-right {
    width: 100%;
    justify-content: space-between;
  }
  .dash-welcome {
    font-size: 0.9rem;
  }
}

/* loading state */
.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: rgba(255,255,255,0.6);
}
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top-color: #13c2c2;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* error alert */
.alert-danger {
  background: rgba(255,139,139,0.15);
  border: 1px solid rgba(255,139,139,0.3);
  color: #ff8b8b;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
}

/* Active users link */
.active-users-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: color 0.2s ease;
}
.active-users-link:hover {
  color: #13c2c2;
}
.toggle-icon {
  transition: transform 0.3s ease;
  font-size: 0.9em;
}
.toggle-icon.rotated {
  transform: rotate(180deg);
}

/* Active users dropdown */
.active-users-dropdown {
  margin-top: 12px;
  padding: 2px;
  background: rgba(0,0,0,0.3);
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.06);
  max-height: 200px;
  overflow-y: auto;
}

.users-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background: rgba(255,255,255,0.02);
  border-radius: 4px;
  font-size: 0.82rem;
}

.user-item-name {
  color: rgba(255,255,255,0.85);
  font-weight: 500;
}

.user-item-time {
  color: rgba(255,255,255,0.5);
  font-size: 0.75rem;
}

.empty-list {
  text-align: center;
  color: rgba(255,255,255,0.4);
  padding: 12px;
  font-size: 0.85rem;
}

/* Slide fade transition */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}
.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}
.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-5px);
  opacity: 0;
}
</style>

<style>
/* Global override for Element Plus dropdown in admin dashboard */
.el-dropdown-menu {
  background: #0a1620 !important;
  border: 1px solid rgba(255,255,255,0.1) !important;
  box-shadow: 0 10px 30px rgba(2,8,12,0.6) !important;
}

.el-dropdown-menu__item {
  color: rgba(255,255,255,0.8) !important;
  font-size: 0.8rem !important;
  padding: 4px 8px !important;
  background: transparent !important;
}

.el-dropdown-menu__item:hover {
  background: rgba(19,194,194,0.15) !important;
  color: #13c2c2 !important;
}

.el-dropdown-menu__item.is-disabled {
  color: rgba(255,255,255,0.3) !important;
}

/* Dialog custom styles for dark theme */
.el-dialog {
  background: linear-gradient(180deg, #0f2330 0%, #071018 100%) !important;
  border: 1px solid rgba(255,255,255,0.1) !important;
  box-shadow: 0 20px 60px rgba(2,8,12,0.8) !important;
}

.el-dialog__header {
  border-bottom: 1px solid rgba(255,255,255,0.05);
  text-align: center !important;
}

.el-dialog__title {
  color: #13c2c2 !important;
  font-weight: 700 !important;
}

.el-dialog__headerbtn {
  top: 2px !important;
  right: 12px !important;
}

.el-dialog__headerbtn .el-dialog__close {
  color: rgba(255,255,255,0.6) !important;
}

.el-dialog__headerbtn:hover .el-dialog__close {
  color: #13c2c2 !important;
}

.el-dialog__footer {
  text-align: center !important;
}

.el-form-item__label {
  color: rgba(255,255,255,0.8) !important;
}

.el-input__wrapper {
  background: rgba(0,0,0,0.25) !important;
  border: 1px solid rgba(255,255,255,0.1) !important;
  box-shadow: none !important;
}

.el-input__wrapper:hover,
.el-input__wrapper.is-focus {
  border-color: #13c2c2 !important;
  box-shadow: 0 0 0 1px rgba(19,194,194,0.2) !important;
}

.el-input__inner {
  color: #fff !important;
}

.el-input__inner::placeholder {
  color: rgba(255,255,255,0.4) !important;
}

.el-button {
  border-color: rgba(255,255,255,0.1) !important;
}

.el-button--default {
  background: rgba(255,255,255,0.05) !important;
  color: rgba(255,255,255,0.8) !important;
}

.el-button--default:hover {
  background: rgba(255,255,255,0.1) !important;
  color: #fff !important;
}

.el-button--primary {
  background: #13c2c2 !important;
  border-color: #13c2c2 !important;
  color: #081014 !important;
}

.el-button--primary:hover {
  background: #0fa6a6 !important;
  border-color: #0fa6a6 !important;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
}
</style>